import { useCallback, useEffect, useRef, useState } from "react";
import {
  LogSet,
  Session as SessionSchema,
  assembleWorkout,
  type Workout,
} from "@jackedlogs/shared";

import type { ProgressApiFetch } from "./types";

// First-page-only budget of session+sets envelopes pulled for the tag-volume
// series. After the gsi2 backfill the sets-per-session read is an indexed
// Query, so this comfortably fits the Lambda 20s budget.
const CHART_SESSION_BUDGET = 200;

// Page size for the chart-workouts fetch. The full budget is pulled across
// several of these instead of one shot so the live raw payload (response
// JSON + JSON.parse result + per-set zod intermediates + assembled
// Workouts) never all exists at once.
const CHART_PAGE_SIZE = 50;

export type UseChartWorkoutsOptions = {
  apiFetch: ProgressApiFetch;
  // Defer the (heavy) first fetch past the host's current interaction —
  // mobile injects InteractionManager.runAfterInteractions, web-rnw injects
  // a requestIdleCallback-based defer (both compete with the rest of the
  // Progress mount's fetches). Optional: omitting it fires immediately.
  // Must return a cancel function.
  schedule?: (run: () => void) => () => void;
};

export type UseChartWorkoutsResult = {
  chartWorkouts: Workout[] | null;
  chartError: string | null;
  chartLoading: boolean;
};

// Shared data hook behind the tag-volume records card (mobile Progress +
// web-rnw). Lazy, single paginated fetch of session+sets envelopes, kept in
// memory for the host lifetime — feeds `buildTagSeries`. Not persisted: the
// heaviest read on the Progress surface, so hosts don't cache it to disk.
export function useChartWorkouts(
  opts: UseChartWorkoutsOptions,
): UseChartWorkoutsResult {
  const apiRef = useRef(opts.apiFetch);
  const scheduleRef = useRef(opts.schedule);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
    scheduleRef.current = opts.schedule;
  });

  const [chartWorkouts, setChartWorkouts] = useState<Workout[] | null>(null);
  const [chartError, setChartError] = useState<string | null>(null);
  const [chartLoading, setChartLoading] = useState(false);

  const loadChartWorkouts = useCallback(async () => {
    if (chartWorkouts !== null || chartLoading) return;
    setChartLoading(true);
    setChartError(null);
    try {
      type Envelope = { session?: unknown; sets?: unknown };
      type Resp = { items?: Envelope[]; cursor?: string | null };
      const out: Workout[] = [];
      let cursor: string | null = null;
      do {
        const base = `/api/sessions?include=sets&limit=${CHART_PAGE_SIZE}`;
        const path = cursor
          ? `${base}&cursor=${encodeURIComponent(cursor)}`
          : base;
        const res: Resp = await apiRef.current<Resp>(path);
        const pageItems = res.items ?? [];
        // An empty page with a live cursor would spin forever. The chart
        // fetch sends no FilterExpression so DDB can't actually return one
        // (a Limit-bounded Query only empties when the cursor is exhausted,
        // which nulls it) — guard anyway so a future default filter on the
        // route can't hang Progress.
        if (pageItems.length === 0) break;
        for (const raw of pageItems) {
          const sParsed = SessionSchema.safeParse(raw.session);
          if (!sParsed.success) continue;
          const setsRaw = Array.isArray(raw.sets) ? raw.sets : [];
          const sets = [];
          for (const sr of setsRaw) {
            const lp = LogSet.safeParse(sr);
            if (lp.success) sets.push(lp.data);
          }
          out.push(assembleWorkout(sParsed.data, sets));
        }
        cursor = res.cursor ?? null;
      } while (cursor !== null && out.length < CHART_SESSION_BUDGET);
      // Enforce the budget exactly: dropped (unparseable) rows can let the
      // final page cross the cap, so keep only the newest budget worth.
      if (out.length > CHART_SESSION_BUDGET) out.length = CHART_SESSION_BUDGET;
      setChartWorkouts(out);
    } catch (e) {
      setChartError(e instanceof Error ? e.message : "progress.chartLoadFailed");
    } finally {
      setChartLoading(false);
    }
  }, [chartWorkouts, chartLoading]);

  // `run` is idempotent (startedRef flips only once it actually fires the
  // load), not the effect registration itself — a StrictMode dev
  // mount→cleanup→remount cancels the FIRST scheduled `run` before it ever
  // executes (the cleanup returned by `schedule`), so the remount must be
  // free to reschedule. Flipping the ref before scheduling (instead of
  // inside `run`) would leave that cancelled schedule as the only attempt
  // and the fetch would never fire on web-rnw dev.
  const startedRef = useRef(false);
  useEffect(() => {
    const schedule = scheduleRef.current;
    const run = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      void loadChartWorkouts();
    };
    if (schedule) return schedule(run);
    run();
    return undefined;
  }, [loadChartWorkouts]);

  return { chartWorkouts, chartError, chartLoading };
}
